import React, { useState } from 'react';
import IntentProgressBox from './IntentProgressBox';

const IntentsSidebar = ({ intents, messages, scenarioName, conversationStatus, errorMessage, onIntentClick }) => {
  const [activeTab, setActiveTab] = useState('intents');
  const getIntentMessages = (intentId) => {
    return messages.filter(msg => msg.intent_id === intentId);
  };

  const getCurrentIntentIndex = () => {
    // Match Rails logic: current intent is the one that has the last message
    if (messages.length === 0) return 0;
    
    const lastMessage = messages[messages.length - 1];
    const currentIntentIndex = intents.findIndex(intent => intent.id === lastMessage.intent_id);
    
    return currentIntentIndex;
  };

  const currentIntentIndex = getCurrentIntentIndex();
  
  // Determine if an intent failed
  const getIntentStatus = (index) => {
    const isConversationFinished = ['passed', 'failed', 'errored'].includes(conversationStatus);
    
    if (conversationStatus === 'failed' && index === currentIntentIndex) {
      // The current intent is the one that failed
      return 'failed';
    } else if (index < currentIntentIndex) {
      return 'completed';
    } else if (index === currentIntentIndex) {
      // If conversation is finished and this is the last intent with messages, it's completed
      if (isConversationFinished && getIntentMessages(intents[index].id).length > 0) {
        return 'completed';
      }
      return 'current';
    }
    return 'pending';
  };
  
  // Debug logging
  console.log('Intent Status:', intents.map((intent, idx) => ({
    position: idx + 1,
    type: intent.intent_type,
    messages: getIntentMessages(intent.id).length,
    isCurrent: idx === currentIntentIndex,
    isCompleted: idx < currentIntentIndex,
    status: getIntentStatus(idx)
  })));

  // Get validated messages (bot messages with validation scores)
  const validatedMessages = messages.filter(m => 
    m.direction === 'bot' && 
    m.validation_score !== null && 
    m.validation_score !== undefined
  );

  return (
    <div className="w-96 bg-slate-900 shadow-2xl overflow-y-auto border-l border-slate-800">
      <div className="relative bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 to-transparent"></div>
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
        
        {/* Tech grid pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>
        
        <div className="relative flex items-center justify-center p-5">
          <h3 className="text-sm font-black text-white uppercase tracking-[0.2em] flex items-center space-x-3">
            <div className="relative">
              <div className="absolute inset-0 bg-cyan-500 blur-lg opacity-20"></div>
              <svg className="relative w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span>{activeTab === 'intents' ? 'Intent Execution' : 'Validation Details'}</span>
            <div className="relative">
              <div className="absolute inset-0 bg-cyan-500 blur-lg opacity-20"></div>
              <svg className="relative w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </h3>
        </div>
        
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent"></div>
      </div>
      
      {/* Tab buttons */}
      <div className="flex border-b border-slate-800">
        <button
          onClick={() => setActiveTab('intents')}
          className={`flex-1 px-4 py-3 text-xs font-medium uppercase tracking-wider transition-all ${
            activeTab === 'intents'
              ? 'text-cyan-400 bg-slate-800/50 border-b-2 border-cyan-400'
              : 'text-gray-400 hover:text-gray-300 hover:bg-slate-800/30'
          }`}
        >
          Intents
        </button>
        <button
          onClick={() => setActiveTab('validation')}
          className={`flex-1 px-4 py-3 text-xs font-medium uppercase tracking-wider transition-all relative ${
            activeTab === 'validation'
              ? 'text-cyan-400 bg-slate-800/50 border-b-2 border-cyan-400'
              : 'text-gray-400 hover:text-gray-300 hover:bg-slate-800/30'
          }`}
        >
          Validation
          {validatedMessages.length > 0 && (
            <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
              {validatedMessages.length}
            </span>
          )}
        </button>
      </div>
      
      <div className="p-4 space-y-3">
        {activeTab === 'intents' ? (
          // Intents tab content
          intents.map((intent, index) => {
            const status = getIntentStatus(index);
            return (
              <IntentProgressBox
                key={intent.id}
                intent={intent}
                messages={getIntentMessages(intent.id)}
                allMessages={messages}
                isCurrent={status === 'current'}
                isCompleted={status === 'completed'}
                isFailed={status === 'failed'}
                position={intent.position || index + 1}
                errorMessage={status === 'failed' ? errorMessage : null}
                onIntentClick={onIntentClick}
              />
            );
          })
        ) : (
          // Validation tab content
          <div className="space-y-3">
            {validatedMessages.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No validation results yet
              </div>
            ) : (
              validatedMessages.map((message, index) => {
                const intent = intents.find(i => i.id === message.intent_id);
                const messageIndex = messages.findIndex(m => m.id === message.id);
                const userMessage = messageIndex > 0 ? messages[messageIndex - 1] : null;
                
                return (
                  <div key={message.id} className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-mono text-cyan-400 bg-cyan-900/30 px-2 py-0.5 rounded border border-cyan-800/50">
                          #{index + 1}
                        </span>
                        {intent && (
                          <span className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                            {intent.intent_type.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                        message.validation_passed
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {message.validation_score.toFixed(2)} {message.validation_passed ? '✓' : '✗'}
                      </span>
                    </div>
                    
                    {/* User message preview */}
                    {userMessage && (
                      <div className="mb-3">
                        <div className="text-xs font-semibold text-gray-400 mb-1">User:</div>
                        <div className="text-xs text-gray-300 bg-slate-700/50 p-2 rounded">
                          {userMessage.body}
                        </div>
                      </div>
                    )}
                    
                    {/* Bot response preview */}
                    <div className="mb-3">
                      <div className="text-xs font-semibold text-gray-400 mb-1">Bot:</div>
                      <div className="text-xs text-gray-300 bg-slate-700/50 p-2 rounded">
                        {message.body}
                      </div>
                    </div>
                    
                    {/* Analysis */}
                    {message.validation_analysis && (
                      <div className="mb-3">
                        <div className="text-xs font-semibold text-cyan-400 mb-1">Analysis:</div>
                        <div className="text-xs text-gray-300 bg-slate-700/30 p-2 rounded border border-slate-700">
                          {message.validation_analysis}
                        </div>
                      </div>
                    )}
                    
                    {/* Reasoning */}
                    {message.validation_reasoning && (
                      <div>
                        <div className="text-xs font-semibold text-cyan-400 mb-1">Reasoning:</div>
                        <div className="text-xs text-gray-300 bg-slate-700/30 p-2 rounded border border-slate-700">
                          {message.validation_reasoning}
                        </div>
                      </div>
                    )}
                    
                    {/* Timestamp */}
                    <div className="mt-3 text-xs text-gray-500">
                      {new Date(message.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IntentsSidebar;